import React from 'react';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { AverageStarRating, LayoutSingleColumn, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from '../_marketingPage/marketingPage.module.css';
import pageCss from './VurderingerPage.module.css';

// The five star-rating levels shown in section 3. Each row renders the
// star icons (filled count = `rating`), the level title, and a short
// description of the experience that warrants that rating.
const STAR_LEVELS = [
  { key: 'l5', rating: 5 },
  { key: 'l4', rating: 4 },
  { key: 'l3', rating: 3 },
  { key: 'l2', rating: 2 },
  { key: 'l1', rating: 1 },
];

const StarLevel = ({ rating, titleId, bodyId }) => (
  <div className={pageCss.starLevel}>
    <div className={pageCss.starLevelHeader}>
      <AverageStarRating rating={rating} />
      <h3 className={pageCss.starLevelTitle}>
        <FormattedMessage id={titleId} />
      </h3>
    </div>
    <p className={pageCss.starLevelBody}>
      <FormattedMessage id={bodyId} />
    </p>
  </div>
);

/**
 * "Slik fungerer vurderinger" page (`/vurderinger`).
 *
 * Explains how the review flow works on Zuprize: both parties leave
 * blind reviews after a completed transaction, the 7-day window
 * (matches `transition/expire-review-period` in the purchase process
 * — see ext/transaction-processes/default-purchase/process.edn), the
 * 1-5 star scale with what each level means, where reviews appear,
 * and Zuprize's no-edit / no-delete policy.
 *
 * Linked from the mobile hamburger menu and footer as "Vurderinger".
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const VurderingerPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled } = props;

  return (
    <Page
      title={intl.formatMessage({ id: 'VurderingerPage.metaTitle' })}
      description={intl.formatMessage({ id: 'VurderingerPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer currentPage="VurderingerPage" />} footer={<FooterContainer />}>
        <article className={css.root}>
          <div className={css.content}>
            <header className={css.hero}>
              <p className={css.heroEyebrow}>
                <FormattedMessage id="VurderingerPage.heroEyebrow" />
              </p>
              <h1 className={css.heroHeading}>
                <FormattedMessage id="VurderingerPage.heroHeading" />
              </h1>
              <p className={css.lede}>
                <FormattedMessage id="VurderingerPage.heroLede" />
              </p>
            </header>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="VurderingerPage.s1Heading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="VurderingerPage.s1Body" />
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="VurderingerPage.s2Heading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="VurderingerPage.s2Body1" />
              </p>
              <p className={css.body}>
                <FormattedMessage id="VurderingerPage.s2Body2" />
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="VurderingerPage.s3Heading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="VurderingerPage.s3Intro" />
              </p>
              <div className={pageCss.starList}>
                {STAR_LEVELS.map(level => (
                  <StarLevel
                    key={level.key}
                    rating={level.rating}
                    titleId={`VurderingerPage.s3${level.key}Title`}
                    bodyId={`VurderingerPage.s3${level.key}Body`}
                  />
                ))}
              </div>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="VurderingerPage.s4Heading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="VurderingerPage.s4Body1" />
              </p>
              <p className={css.body}>
                <FormattedMessage id="VurderingerPage.s4Body2" />
              </p>
            </section>

            <section className={css.section}>
              <h2 className={css.sectionHeading}>
                <FormattedMessage id="VurderingerPage.s5Heading" />
              </h2>
              <p className={css.body}>
                <FormattedMessage id="VurderingerPage.s5Body" />
              </p>
            </section>
          </div>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const VurderingerPage = connect(mapStateToProps)(VurderingerPageComponent);

export { VurderingerPageComponent };
export default VurderingerPage;
